/**
 * Authentication Hook
 * Phase 6: Frontend Application
 * Phase 7.6: Token expiration validation
 *
 * Provides authentication state and actions for components.
 * Uses MSAL React hooks internally.
 * Validates token expiration before considering user authenticated.
 */

'use client';

import { InteractionStatus } from '@azure/msal-browser';
import {
  useAccount,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import { useCallback, useEffect, useMemo } from 'react';
import { logger } from '../logger';
import { apiTokenRequest, isAuthConfigured, loginRequest } from './msal-config';

/**
 * Check if the ID token has expired
 * Returns true if expired or token data is invalid
 */
function isTokenExpired(account: ReturnType<typeof useAccount>): boolean {
  if (!account) return true;

  try {
    const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
    if (!idTokenClaims) return true;

    // Get expiration time from token claims (exp is in seconds since epoch)
    const exp = idTokenClaims.exp as number | undefined;
    if (!exp) return true;

    // Check if token has expired (with 5 minute buffer to avoid edge cases)
    const now = Math.floor(Date.now() / 1000);
    const expirationBuffer = 5 * 60; // 5 minutes in seconds
    return now >= (exp - expirationBuffer);
  } catch (error) {
    logger.error('Error checking token expiration:', error);
    return true; // Assume expired on error
  }
}

export interface User {
  id: string;
  name: string;
  email: string;
  tenantId: string;
  roles: string[];
  groups: string[]; // Entra ID Security Group Object IDs
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
}

export interface AuthActions {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

/**
 * Hook for authentication state and actions
 */
export function useAuth(): AuthState & AuthActions {
  const isMsalAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const activeAccount = accounts[0] ?? null;
  const account = useAccount(activeAccount as Parameters<typeof useAccount>[0]);

  // Check if token has expired
  const tokenExpired = useMemo(() => {
    if (!isMsalAuthenticated || !account) return false;
    return isTokenExpired(account);
  }, [isMsalAuthenticated, account]);

  // Validate authentication: user must be authenticated AND token must not be expired
  const isAuthenticated = useMemo(() => {
    return isMsalAuthenticated && !tokenExpired;
  }, [isMsalAuthenticated, tokenExpired]);

  // Parse user from account
  const user = useMemo((): User | null => {
    if (!account || tokenExpired) return null;

    // Extract roles and groups from ID token claims
    const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
    const roles = (idTokenClaims?.roles as string[]) || [];
    // Groups come as 'groups' claim when configured in Token Configuration
    const groups = (idTokenClaims?.groups as string[]) || [];

    return {
      id: account.localAccountId,
      name: account.name || 'Unknown User',
      email: account.username,
      tenantId: account.tenantId,
      roles,
      groups,
    };
  }, [account, tokenExpired]);

  // Check if any interaction is in progress
  const isLoading = inProgress !== InteractionStatus.None;

  // Login action
  const login = useCallback(async (): Promise<void> => {
    if (!isAuthConfigured()) {
      logger.warn('Auth not configured - login disabled');
      return;
    }

    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  }, [instance]);

  // Logout action
  const logout = useCallback(async (silent: boolean = false): Promise<void> => {
    if (!isAuthConfigured()) {
      return;
    }

    try {
      if (silent) {
        // Silent logout for expired tokens - clear cache and redirect without Microsoft prompt
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          // Clear the MSAL cache for the current account
          await instance.clearCache();
        }
        // Redirect to home page without going through Microsoft logout
        window.location.href = window.location.origin;
      } else {
        // Normal logout - go through full Microsoft logout flow
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        });
      }
    } catch (error) {
      logger.error('Logout error:', error);
      // Fallback: force redirect even if logout fails
      if (silent) {
        window.location.href = window.location.origin;
      } else {
        throw error;
      }
    }
  }, [instance]);

  // Automatically logout if token has expired
  useEffect(() => {
    if (tokenExpired && isMsalAuthenticated && !isLoading) {
      logger.warn('Token has expired - logging out silently and redirecting to login');
      // Use silent logout to avoid Microsoft account picker
      logout(true).catch((error) => {
        logger.error('Auto-logout failed, forcing redirect:', error);
        // Force redirect as last resort
        window.location.href = window.location.origin;
      });
    }
  }, [tokenExpired, isMsalAuthenticated, isLoading, logout]);

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthConfigured() || !account) {
      return null;
    }

    try {
      // Try silent token acquisition first
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      logger.warn('Silent token acquisition failed, trying redirect:', error);
      
      // Fall back to redirect
      try {
        await instance.acquireTokenRedirect({
          ...apiTokenRequest,
          account,
        });
        return null; // Will redirect
      } catch (redirectError) {
        logger.error('Token acquisition failed:', redirectError);
        return null;
      }
    }
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading,
    user,
    error: null,
    login,
    logout,
    getAccessToken,
  };
}

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(role: string): boolean {
  const { user } = useAuth();
  return user?.roles?.includes(role) ?? false;
}

/**
 * Hook to check if user has any of the specified roles
 */
export function useHasAnyRole(roles: string[]): boolean {
  const { user } = useAuth();
  return roles.some(role => user?.roles?.includes(role)) ?? false;
}

/**
 * Role constants matching API roles
 */
export const ROLES = {
  ADMIN: 'admin',
  TRUSTED_EDITOR: 'trusted_editor',
  CONTRIBUTOR: 'contributor',
  VIEWER: 'viewer',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/**
 * Hook to check if user is in a specific Entra ID security group
 */
export function useIsInGroup(groupId: string): boolean {
  const { user } = useAuth();
  return user?.groups?.includes(groupId) ?? false;
}

/**
 * Hook to check if user is in any of the specified groups
 */
export function useIsInAnyGroup(groupIds: string[]): boolean {
  const { user } = useAuth();
  return groupIds.some(groupId => user?.groups?.includes(groupId)) ?? false;
}
